import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchAnchorConfig, fetchCombinations, fetchCountries, fetchFilterOptions, fetchQueryEstimate, fetchStats, fetchSubMarkets, queryBudgetEstimateFromError } from "../api";
import { AnchorSelector, DateRangeModal, LoadingSpinner, MultiSelect, QueryBudgetNotice, SingleSelect } from "../components";
import { ALL_SALES_CHANNELS } from "../components/FilterBar";
import { ItemNoInfo } from "../components/ComboDescription";
import { HelpHint } from "../components/HelpHint";
import { useApi, useSharedParams } from "../hooks";
import { useIsMobile } from "../hooks/useIsMobile";
import { ALL_FRAME_TYPES } from "../hooks/useSharedParams";
import type { Combination } from "../types";
import { formatCountry } from "../utils/countries";
import { normalizeColourSpelling } from "../utils/colourLabels";
import { COMBO_QUANTITY_COL, SHARE_PCT_COL } from "../utils/combinationListTooltips";
import { exclusiveEndDate, periodDateRange, periodStartDate, buildClosedTertials, parseTertialDates } from "../utils/dates";
import { canLoadParquetQueries, isFrameTypesReady } from "../utils/loadGating";
import styles from "./TopCombinations.module.css";

const FAVORITE_COUNTRY_KEY = "mcc-favorite-country";
const FAVORITE_SUB_MARKET_KEY = "mcc-favorite-sub-market";
const FAVORITE_PERIOD_KEY = "mcc-favorite-period";
const FAVORITE_LIMIT_KEY = "mcc-favorite-limit";
const REVENUE_COL_TOOLTIP = "Total revenue: combination value multiplied by matching receipts, EUR";

// IKEA fiscal year starts Sep 1 — FY26 = Sep 2025 – Aug 2026
const now = new Date();
const fyYear = (now.getMonth() >= 8 ? now.getFullYear() + 1 : now.getFullYear()) % 100;

interface PeriodOption {
    value: string;
    label: string;
    tooltip?: string;
    disabled?: boolean;
    disabledTooltip?: string;
}

function buildPeriods(dataAvailableFrom?: string): PeriodOption[] {
    const tertials = buildClosedTertials();
    const periods: PeriodOption[] = [
        { value: "1w", label: "Last week" },
        { value: "4w", label: "4 weeks" },
        { value: "8w", label: "8 weeks" },
        { value: "12w", label: "12 weeks" },
        { value: "16w", label: "16 weeks" },
        { value: "52w", label: "52 weeks" },
        ...tertials,
        { value: "fy", label: `Year to date`, tooltip: `Year to date — fiscal year FY${fyYear} so far (Sep 1, ${2000 + fyYear - 1} – yesterday)` },
        { value: "custom", label: "Custom…" },
    ];
    return periods.map(p => {
        if (dataAvailableFrom && p.value !== "custom") {
            const startDate = periodStartDate(p.value);
            if (startDate && startDate < dataAvailableFrom) {
                return { ...p, disabled: true, disabledTooltip: `Data not available before ${dataAvailableFrom}` };
            }
        }
        if (!p.tooltip) {
            const range = periodDateRange(p.value);
            if (range) return { ...p, tooltip: `Closed weeks: ${range}` };
        }
        return p;
    });
}

const LIMITS = [
    { value: "10", label: "Top 10" },
    { value: "25", label: "Top 25" },
    { value: "50", label: "Top 50" },
    { value: "100", label: "Top 100" },
];

function getStored(key: string): string | null {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
}

function setStored(key: string, value: string | null) {
    try {
        if (value === null) {
            localStorage.removeItem(key);
        } else {
            localStorage.setItem(key, value);
        }
    } catch { /* ignore */ }
}

// Component presence is a non-empty multiselect of these two values.
// Both selected = no filter (show all); one selected = filter to that case.
const COMPONENT_FILTER_OPTIONS = ["with", "without"];
const COMPONENT_FILTER_LABELS: Record<string, string> = { with: "With", without: "Without" };
const COMPONENT_FILTER_TOOLTIPS: Record<string, string> = {
    with: "Include combinations that have this component",
    without: "Include combinations without this component",
};

// Map the tri-value API state (true=with only, false=without only, null=both)
// to/from the multiselect's selected values. Selection is kept non-empty.
function toComponentSelected(v: boolean | null): string[] {
    if (v === true) return ["with"];
    if (v === false) return ["without"];
    return ["with", "without"];
}

function fromComponentSelected(selected: string[]): boolean | null {
    const hasWith = selected.includes("with");
    const hasWithout = selected.includes("without");
    if (hasWith && !hasWithout) return true;
    if (hasWithout && !hasWith) return false;
    return null;
}

function getDisplayItems(combo: Combination) {
    const items: { qty: number; name: string; itemNo: string; productType?: string | null; color?: string | null; isAnchor?: boolean }[] = [];
    const companionItemNos = new Set(combo.companion_items.map((c) => c.item_no));
    const itemAttrs = combo.item_attributes || {};

    for (const itemNo of combo.anchor_items) {
        const attr = itemAttrs[itemNo];
        if (attr) {
            const namePart = attr.EnglishName
                ? `${attr.EnglishName} ${attr.MeasureReferenceMetric || ""}`.trim()
                : null;
            items.push({ qty: 1, name: namePart || "", itemNo, productType: attr.ProductType, color: attr.Color, isAnchor: true });
        }
    }

    if (items.length === 0) {
        for (const [itemNo, attr] of Object.entries(itemAttrs)) {
            if (!companionItemNos.has(itemNo) && attr.EnglishName === combo.anchor) {
                const namePart = attr.EnglishName
                    ? `${attr.EnglishName} ${attr.MeasureReferenceMetric || ""}`.trim()
                    : null;
                items.push({ qty: 1, name: namePart || "", itemNo, productType: attr.ProductType, color: attr.Color, isAnchor: true });
            }
        }
    }

    if (items.length === 0 && combo.total_width_cm) {
        items.push({ qty: 1, name: `${combo.anchor} (${combo.total_width_cm}cm total width)`, itemNo: "", isAnchor: true });
    }

    for (const item of combo.companion_items.filter((i) => i.qty > 0)) {
        const namePart = item.name ? `${item.name} ${item.dimensions || ""}`.trim() : null;
        items.push({ qty: item.qty, name: namePart || "", itemNo: item.item_no, productType: item.product_type, color: item.color, isAnchor: false });
    }

    // Aggregate identical items (same itemNo) and sum their quantities
    const aggregated = new Map<string, typeof items[0]>();
    for (const item of items) {
        const key = item.itemNo ? `${item.itemNo}:${item.isAnchor}` : `_${items.indexOf(item)}`;
        const existing = aggregated.get(key);
        if (existing) {
            existing.qty += item.qty;
        } else {
            aggregated.set(key, { ...item });
        }
    }
    return Array.from(aggregated.values());
}

export function TopCombinations() {
    const {
        anchor,
        selectedCountries, selectedSalesChannels,
        subMarketsParam: subMarketsFromUrl,
        period, frameTypes, setCountries,
        setSubMarkets: setSharedSubMarkets,
        setSalesChannels, setPeriod: setSharedPeriod, setFrameTypes,
        homeCountryResolved,
        countriesFromUrl,
    } = useSharedParams(ALL_SALES_CHANNELS);

    const [favCountry, setFavCountry] = useState<string | null>(() => getStored(FAVORITE_COUNTRY_KEY));
    const [favSubMarket, setFavSubMarket] = useState<string | null>(() => getStored(FAVORITE_SUB_MARKET_KEY));
    const [favPeriod, setFavPeriod] = useState<string | null>(() => getStored(FAVORITE_PERIOD_KEY));
    const [favLimit, setFavLimit] = useState<string | null>(() => getStored(FAVORITE_LIMIT_KEY));

    // Page-specific state (not shared with Dashboard)
    const [limit, setLimit] = useState(favLimit || "10");
    const [sortBy, setSortBy] = useState("quantity");
    const [selectedColors, setSelectedColors] = useState<string[]>([]);
    const [selectedWidths, setSelectedWidths] = useState<string[]>([]);
    const [selectedHeights, setSelectedHeights] = useState<string[]>([]);
    const [showDateModal, setShowDateModal] = useState(false);
    const [hasDoors, setHasDoors] = useState<boolean | null>(true);
    const [hasInterior, setHasInterior] = useState<boolean | null>(true);
    const [hasLighting, setHasLighting] = useState<boolean | null>(null);
    const [hasKnobsHandles, setHasKnobsHandles] = useState<boolean | null>(null);
    const [hasWallMounting, setHasWallMounting] = useState<boolean | null>(null);

    const { data: countries } = useApi(() => fetchCountries(anchor), [anchor]);
    const { data: anchorConfig } = useApi(() => fetchAnchorConfig(anchor), [anchor], true);
    const hasFrameTypes = !!anchorConfig && anchorConfig.frame_type_categories.length > 0;
    const anchorConfigReady = !!anchorConfig && anchorConfig.anchor === anchor;
    // When frame_types from URL don't match the current anchor's categories,
    // fall back to all categories (show everything by default).
    const effectiveFrameTypes = useMemo(() => {
        if (!anchorConfig || anchorConfig.frame_type_categories.length === 0)
            return frameTypes ?? ALL_FRAME_TYPES;
        if (!frameTypes)
            return anchorConfig.default_frame_types ?? anchorConfig.frame_type_categories;
        const valid = frameTypes.filter(t => anchorConfig.frame_type_categories.includes(t));
        return valid.length > 0 ? valid : (anchorConfig.default_frame_types ?? anchorConfig.frame_type_categories);
    }, [frameTypes, anchorConfig]);
    const anchorFlags = useMemo(
        () => new Set(anchorConfig?.feature_flags || []),
        [anchorConfig],
    );
    const hasFlag = (f: string) => anchorFlags.has(f);
    const periods = useMemo(
        () => buildPeriods(anchorConfig?.data_available_from || undefined),
        [anchorConfig],
    );

    // Auto-correct period when it becomes disabled after anchor switch
    useEffect(() => {
        const daf = anchorConfig?.data_available_from;
        if (!daf || period === "custom" || period.startsWith("custom:")) return;
        const startDate = periodStartDate(period);
        if (startDate && startDate < daf) {
            const fallbacks = ["4w", "1w"];
            const fallback = fallbacks.find(p => {
                const s = periodStartDate(p);
                return !s || s >= daf;
            }) || "1w";
            setSharedPeriod(fallback);
        }
    }, [anchorConfig?.data_available_from, period, setSharedPeriod]);

    const frameTypesForApi = hasFrameTypes ? (effectiveFrameTypes.join(",") || undefined) : undefined;

    // Only send has_* filters for flags this anchor actually supports;
    // sending e.g. has_interior=true to BESTÅ (which has no interior flag)
    // would always return zero rows.
    const hasDoorsForApi = hasFlag("has_doors") ? (hasDoors ?? undefined) : undefined;
    const hasInteriorForApi = hasFlag("has_interior") ? (hasInterior ?? undefined) : undefined;
    const hasLightingForApi = hasFlag("has_lighting") ? (hasLighting ?? undefined) : undefined;
    const hasKnobsHandlesForApi = hasFlag("has_knobs_handles") ? (hasKnobsHandles ?? undefined) : undefined;
    const hasWallMountingForApi = hasFlag("has_wall_mounting") ? (hasWallMounting ?? undefined) : undefined;

    // If the selected country isn't available for the current anchor, pick the
    // first available country so we don't fire queries that 404.
    useEffect(() => {
        if (!countries || countries.length === 0) return;
        // Only wait for the home country to resolve when the selection came from
        // the home-country/favorite/DE default (no explicit URL param) —
        // otherwise a late-resolving home country would be clobbered by "first
        // available". When the URL carries an explicit (invalid) countries
        // param, home country is irrelevant, so heal immediately instead of
        // waiting up to the /auth/me timeout and firing 404s in the meantime.
        if (!countriesFromUrl && !homeCountryResolved) return;
        const valid = selectedCountries.filter((c) => countries.includes(c));
        if (valid.length === 0) {
            setCountries([countries[0]]);
        } else if (valid.length !== selectedCountries.length) {
            setCountries(valid);
        }
    }, [countries, selectedCountries, setCountries, homeCountryResolved, countriesFromUrl]);

    const countryParam = selectedCountries.join(",");


    const { data: subMarkets, error: subMarketsError } = useApi(
        () => fetchSubMarkets(countryParam),
        [countryParam],
        countryParam.length > 0
    );

    // Default to all sub markets selected when sub market list loads/changes.
    // If the sub-markets endpoint fails, treat as "no filter" so the page isn't blocked.
    const allSubMarketCodes = useMemo(() => (subMarkets || []).map(s => s.code), [subMarkets]);
    const selectedSubMarkets = subMarketsFromUrl !== null
        ? subMarketsFromUrl.split(",").filter(code => allSubMarketCodes.includes(code))
        : allSubMarketCodes;

    // Build API params from selected filters
    const isCustomPeriod = period.startsWith("custom:");
    const periodParams = useMemo(() => {
        if (isCustomPeriod) {
            const [, start, end] = period.split(":");
            return { start_date: start, end_date: exclusiveEndDate(end) };
        }
        const tertial = parseTertialDates(period);
        if (tertial) return tertial;
        return { period };
    }, [period, isCustomPeriod]);
    const customReady = !isCustomPeriod || ("start_date" in periodParams && !!periodParams.start_date);
    const customDates = isCustomPeriod ? period.split(":").slice(1) : undefined;
    const displayPeriod = isCustomPeriod ? "custom" : period;

    const salesChannelParam = selectedSalesChannels.length > 0 && selectedSalesChannels.length < ALL_SALES_CHANNELS.length
        ? selectedSalesChannels.join(",")
        : undefined;

    const subMarketMap = useMemo(() => {
        return new Map((subMarkets || []).map(s => [s.code, s]));
    }, [subMarkets]);


    const formatSubMarketOption = useCallback((code: string) => {
        const sm = subMarketMap.get(code);
        return sm?.name ? `${code} – ${sm.name}` : code;
    }, [subMarketMap]);


    const subMarketParam = selectedSubMarkets.length > 0 && selectedSubMarkets.length < allSubMarketCodes.length
        ? selectedSubMarkets.join(",")
        : undefined;

    const frameTypesReady = isFrameTypesReady(frameTypes, anchorConfigReady);
    const selectionReady =
        countryParam.length > 0 &&
        selectedSalesChannels.length > 0 &&
        (selectedSubMarkets.length > 0 || !!subMarketsError) &&
        frameTypesReady &&
        effectiveFrameTypes.length > 0;

    const queryEstimateKey = `${anchor}:${countryParam}:${period}`;
    const {
        data: queryEstimateResult,
        loading: queryEstimateLoading,
        error: queryEstimateError,
    } = useApi(
        () => fetchQueryEstimate({
            anchor,
            country: countryParam,
            ...periodParams,
        }).then((estimate) => ({ key: queryEstimateKey, estimate })),
        [queryEstimateKey, customReady],
        customReady && selectionReady
    );
    const queryEstimate = queryEstimateResult?.key === queryEstimateKey ? queryEstimateResult.estimate : null;
    const queryBudgetBlocked = selectionReady && !queryEstimateLoading && queryEstimate?.allowed === false;
    const queryBudgetUnavailable = selectionReady && !queryEstimateLoading && !!queryEstimateError;
    const filtersReady = canLoadParquetQueries(
        selectionReady,
        queryEstimateLoading,
        queryEstimate?.allowed,
        queryEstimateKey,
        queryEstimateResult?.key
    );

    const { data: filterOptions } = useApi(
        () => fetchFilterOptions({
            anchor,
            country: countryParam,
            ...periodParams,
            sub_market: subMarketParam,
            frame_types: frameTypesForApi,
            sales_channels: salesChannelParam,
        }),
        [anchor, countryParam, period, subMarketParam, frameTypesForApi, salesChannelParam, customReady],
        customReady && filtersReady
    );

    const countryOptions = useMemo(
        () => (countries || [favCountry || "DE"]),
        [countries, favCountry]
    );

    const colorOptions = useMemo(
        () => filterOptions?.colors || [],
        [filterOptions]
    );

    const widthOptions = useMemo(
        () => (filterOptions?.total_widths || []).map(String),
        [filterOptions]
    );

    const heightOptions = useMemo(
        () => (filterOptions?.heights || []).map(String),
        [filterOptions]
    );

    // Clear stale selections when available options change (e.g. toggling Corner
    // removes some widths — don't keep a width selected that no longer exists)
    useEffect(() => {
        if (!filterOptions) return;
        const validColors = new Set(filterOptions.colors || []);
        const validWidths = new Set((filterOptions.total_widths || []).map(String));
        const validHeights = new Set((filterOptions.heights || []).map(String));
        setSelectedColors(prev => {
            const kept = prev.filter(c => validColors.has(c));
            return kept.length === prev.length ? prev : kept;
        });
        setSelectedWidths(prev => {
            const kept = prev.filter(w => validWidths.has(w));
            return kept.length === prev.length ? prev : kept;
        });
        setSelectedHeights(prev => {
            const kept = prev.filter(h => validHeights.has(h));
            return kept.length === prev.length ? prev : kept;
        });
    }, [filterOptions]);

    const colorParam = selectedColors.length > 0 && selectedColors.length < colorOptions.length
        ? selectedColors.join(",")
        : undefined;
    const widthParam = selectedWidths.length > 0 && selectedWidths.length < widthOptions.length
        ? selectedWidths.join(",")
        : undefined;
    const heightParam = selectedHeights.length > 0 && selectedHeights.length < heightOptions.length
        ? selectedHeights.join(",")
        : undefined;

    const {
        data: combinations,
        loading,
        error,
    } = useApi(
        () => fetchCombinations({
            anchor,
            country: countryParam,
            ...periodParams,
            limit: Number(limit),
            sub_market: subMarketParam,
            color: colorParam,
            width_cm: widthParam,
            height_cm: heightParam,
            has_doors: hasDoorsForApi,
            has_interior: hasInteriorForApi,
            has_lighting: hasLightingForApi,
            has_knobs_handles: hasKnobsHandlesForApi,
            has_wall_mounting: hasWallMountingForApi,
            frame_types: frameTypesForApi,
            sort_by: sortBy,
            sales_channels: salesChannelParam,
        }),
        [anchor, countryParam, period, subMarketParam, colorParam, widthParam, heightParam, limit, hasDoorsForApi, hasInteriorForApi, hasLightingForApi, hasKnobsHandlesForApi, hasWallMountingForApi, frameTypesForApi, sortBy, salesChannelParam, customReady],
        customReady && filtersReady
    );
    const requestBudgetEstimate = queryBudgetEstimateFromError(error);
    const displayedBudgetEstimate = queryBudgetBlocked ? queryEstimate : requestBudgetEstimate;

    const { data: stats, loading: statsLoading } = useApi(
        () => fetchStats({
            anchor,
            country: countryParam,
            ...periodParams,
            sub_market: subMarketParam,
            color: colorParam,
            width_cm: widthParam,
            height_cm: heightParam,
            frame_types: frameTypesForApi,
            has_doors: hasDoorsForApi,
            has_interior: hasInteriorForApi,
            has_lighting: hasLightingForApi,
            has_knobs_handles: hasKnobsHandlesForApi,
            has_wall_mounting: hasWallMountingForApi,
            sales_channels: salesChannelParam,
        }),
        [anchor, countryParam, period, subMarketParam, colorParam, widthParam, heightParam, frameTypesForApi, hasDoorsForApi, hasInteriorForApi, hasLightingForApi, hasKnobsHandlesForApi, hasWallMountingForApi, salesChannelParam, customReady],
        customReady && filtersReady
    );

    const handleCountriesChange = useCallback((values: string[]) => {
        setCountries(values);
        setSelectedColors([]);
        setSelectedWidths([]);
        setSelectedHeights([]);
    }, [setCountries]);


    const handleSubMarketsChange = useCallback((values: string[]) => {
        setSharedSubMarkets(values, allSubMarketCodes);
    }, [setSharedSubMarkets, allSubMarketCodes]);

    const handlePeriodChange = useCallback((value: string) => {
        if (value === "custom") {
            setShowDateModal(true);
        } else {
            setSharedPeriod(value);
        }
    }, [setSharedPeriod]);

    const handleSalesChannelsChange = useCallback((values: string[]) => {
        setSalesChannels(values, ALL_SALES_CHANNELS);
    }, [setSalesChannels]);

    const handleLimitChange = useCallback((value: string) => {
        setLimit(value);
    }, []);

    const handleFavCountry = useCallback((v: string | null) => { setFavCountry(v); setStored(FAVORITE_COUNTRY_KEY, v); }, []);
    const handleFavSubMarket = useCallback((v: string | null) => { setFavSubMarket(v); setStored(FAVORITE_SUB_MARKET_KEY, v); }, []);
    const handleFavPeriod = useCallback((v: string | null) => { setFavPeriod(v); setStored(FAVORITE_PERIOD_KEY, v); }, []);
    const handleFavLimit = useCallback((v: string | null) => { setFavLimit(v); setStored(FAVORITE_LIMIT_KEY, v); }, []);

    const handleColorsChange = useCallback((selected: string[]) => {
        setSelectedColors(selected);
    }, []);

    const handleWidthsChange = useCallback((selected: string[]) => {
        setSelectedWidths(selected);
    }, []);

    const handleHeightsChange = useCallback((selected: string[]) => {
        setSelectedHeights(selected);
    }, []);

    const filteredCombinations = (combinations || []);

    // Show a local-currency revenue column only when every combination in the
    // current selection shares one non-EUR currency. If the selected
    // countries use different currencies, fall back to the EUR-only column.
    const localCurrency = useMemo(() => {
        const currencies = new Set(
            filteredCombinations.map((c) => c.combination_currency || "EUR")
        );
        if (currencies.size !== 1) return null;
        const [currency] = currencies;
        return currency !== "EUR" ? currency : null;
    }, [filteredCombinations]);

    // Extract image URLs from item_attributes (embedded in parquet data)
    const imageUrls = useMemo(() => {
        const urls: Record<string, { thumb: string; preview: string }> = {};
        for (const combo of filteredCombinations) {
            const attrs = combo.item_attributes || {};
            for (const [itemNo, attr] of Object.entries(attrs)) {
                if (attr.ImageUrlThumb && !(itemNo in urls)) {
                    urls[itemNo] = {
                        thumb: attr.ImageUrlThumb,
                        preview: attr.ImageUrlPreview || attr.ImageUrlThumb,
                    };
                }
            }
        }
        return urls;
    }, [filteredCombinations]);

    const [hoverImg, setHoverImg] = useState<{ src: string; x: number; y: number } | null>(null);
    const [clickPreview, setClickPreview] = useState<string | null>(null);

    const onThumbEnter = useCallback((e: React.MouseEvent<HTMLImageElement>, src: string) => {
        const r = e.currentTarget.getBoundingClientRect();
        setHoverImg({ src, x: r.right + 8, y: r.top });
    }, []);
    const onThumbLeave = useCallback(() => setHoverImg(null), []);
    const onThumbClick = useCallback((src: string) => {
        setClickPreview(src);
        setHoverImg(null);
    }, []);

    const isMobile = useIsMobile();
    const [filtersOpen, setFiltersOpen] = useState(true);
    const dataError = error && !displayedBudgetEstimate && !queryBudgetUnavailable
        ? error
        : null;

    // Wait for the anchor config — it determines which has_* filters are
    // valid for this anchor and whether frame_types is meaningful. Rendering
    // before it loads briefly sends PAX-only filters for non-PAX anchors.
    if (!anchorConfig) {
        return (
            <div className={styles.page}>
                <LoadingSpinner message="Loading…" />
            </div>
        );
    }

    return (
        <div className={`${styles.page} ${!filtersReady || displayedBudgetEstimate ? styles.staleResults : ""}`}>
            <div className={styles.intro}>
                <h2 className={styles.heading}>Top {limit} {anchor} Combinations</h2>
                <p className={styles.subtitle}>
                    {sortBy === "revenue"
                        ? `${anchor} configurations ranked by total revenue.`
                        : `The most popular ${anchor} configurations customers buy.`}
                </p>
            </div>

            <div className={`${styles.filterRegion} ${isMobile ? styles.filterRegionMobile : ""} ${isMobile && filtersOpen ? styles.filterRegionMobileOpen : ""}`}>
                {isMobile && (
                    <button
                        type="button"
                        className={styles.filterToggle}
                        onClick={() => setFiltersOpen((o) => !o)}
                        aria-expanded={filtersOpen}
                    >
                        <span>Filters</span>
                        <span className={styles.filterChevron} aria-hidden="true">{filtersOpen ? "▲" : "▼"}</span>
                    </button>
                )}
                {(!isMobile || filtersOpen) && (
                  <>
                    <div className={styles.filters}>
                        <AnchorSelector />
                <MultiSelect
                    label="Retail Unit"
                    options={countryOptions}
                    selected={selectedCountries}
                    onChange={handleCountriesChange}
                    placeholder="Select retail units..."
                    allSelectedText="All retail units"
                    favorite={favCountry ?? undefined}
                    onFavoriteChange={handleFavCountry}
                    showSelectedSummary={true}
                    formatOption={formatCountry}
                    tooltip="Select one or more retail units to include"
                />
                <MultiSelect
                    label="Sales Channel"
                    options={ALL_SALES_CHANNELS}
                    selected={selectedSalesChannels}
                    onChange={handleSalesChannelsChange}
                    placeholder="Select channels..."
                    allSelectedText="All channels"
                    tooltip="Filter by sales channel: Store (physical), Online, or Remote"
                />
                <MultiSelect
                    label="Sub-market"
                    options={allSubMarketCodes}
                    selected={selectedSubMarkets}
                    onChange={handleSubMarketsChange}
                    placeholder="Select sub-markets..."
                    allSelectedText="All sub-markets"
                    favorite={favSubMarket ?? undefined}
                    onFavoriteChange={handleFavSubMarket}
                    formatOption={formatSubMarketOption}
                    showSelectedSummary={true}
                    formatSummaryItem={(code) => code}
                    tooltip="Filter by sub-market"
                    disabledMessage={selectedCountries.length === 0 ? "Select a retail unit first" : undefined}
                />
                <SingleSelect
                    label="Period"
                    options={periods}
                    value={displayPeriod}
                    onChange={handlePeriodChange}
                    subtitle={periodDateRange(period)}
                    tooltip="Time period to aggregate sales data over"
                    favorite={favPeriod}
                    onFavoriteChange={handleFavPeriod}
                />
                {showDateModal && (
                    <DateRangeModal
                        initialStart={customDates?.[0]}
                        initialEnd={customDates?.[1]}
                        minAllowedDate={anchorConfig?.data_available_from || undefined}
                        onCancel={() => setShowDateModal(false)}
                        onApply={(start, end) => {
                            setShowDateModal(false);
                            setSharedPeriod(`custom:${start}:${end}`);
                        }}
                    />
                )}
                <SingleSelect
                    label="Show"
                    options={LIMITS}
                    value={limit}
                    onChange={handleLimitChange}
                    tooltip="Number of top combinations to display"
                    favorite={favLimit}
                    onFavoriteChange={handleFavLimit}
                />
                <MultiSelect
                    label="Frame colour"
                    options={colorOptions}
                    selected={selectedColors.length > 0 ? selectedColors : colorOptions}
                    onChange={handleColorsChange}
                    placeholder="All colours"
                    allSelectedText="All colours"
                    formatOption={normalizeColourSpelling}
                    tooltip="Filter by product family frame colour"
                />
                <MultiSelect
                    label="Total width"
                    options={widthOptions}
                    selected={selectedWidths.length > 0 ? selectedWidths : widthOptions}
                    onChange={handleWidthsChange}
                    placeholder="All widths"
                    allSelectedText="All widths"
                    formatOption={(w) => `${w} cm`}
                    tooltip="Filter by total wardrobe width (sum of all frames)"
                />
                <MultiSelect
                    label="Frame height"
                    options={heightOptions}
                    selected={selectedHeights.length > 0 ? selectedHeights : heightOptions}
                    onChange={handleHeightsChange}
                    placeholder="All heights"
                    allSelectedText="All heights"
                    formatOption={(h) => `${h} cm`}
                    tooltip="Filter by product family frame height"
                />
            </div>

            <div className={styles.featureFilters}>
                {hasFlag("has_doors") && (
                    <MultiSelect
                        label="Doors"
                        options={COMPONENT_FILTER_OPTIONS}
                        selected={toComponentSelected(hasDoors)}
                        onChange={(sel) => { if (sel.length > 0) setHasDoors(fromComponentSelected(sel)); }}
                        formatOption={(o) => COMPONENT_FILTER_LABELS[o]}
                        optionTooltips={COMPONENT_FILTER_TOOLTIPS}
                        allSelectedText="With & without"
                        tooltip="Filter combinations by door component"
                    />
                )}
                {hasFlag("has_interior") && (
                    <MultiSelect
                        label="Interior"
                        options={COMPONENT_FILTER_OPTIONS}
                        selected={toComponentSelected(hasInterior)}
                        onChange={(sel) => { if (sel.length > 0) setHasInterior(fromComponentSelected(sel)); }}
                        formatOption={(o) => COMPONENT_FILTER_LABELS[o]}
                        optionTooltips={COMPONENT_FILTER_TOOLTIPS}
                        allSelectedText="With & without"
                        tooltip="Filter combinations by interior component"
                    />
                )}
                {hasFlag("has_lighting") && (
                    <MultiSelect
                        label="Lighting"
                        options={COMPONENT_FILTER_OPTIONS}
                        selected={toComponentSelected(hasLighting)}
                        onChange={(sel) => { if (sel.length > 0) setHasLighting(fromComponentSelected(sel)); }}
                        formatOption={(o) => COMPONENT_FILTER_LABELS[o]}
                        optionTooltips={COMPONENT_FILTER_TOOLTIPS}
                        allSelectedText="With & without"
                        tooltip="Filter combinations by lighting component"
                    />
                )}
                {hasFlag("has_knobs_handles") && (
                    <MultiSelect
                        label="Knobs & Handles"
                        options={COMPONENT_FILTER_OPTIONS}
                        selected={toComponentSelected(hasKnobsHandles)}
                        onChange={(sel) => { if (sel.length > 0) setHasKnobsHandles(fromComponentSelected(sel)); }}
                        formatOption={(o) => COMPONENT_FILTER_LABELS[o]}
                        optionTooltips={COMPONENT_FILTER_TOOLTIPS}
                        allSelectedText="With & without"
                        tooltip="Filter combinations by knobs & handles component"
                    />
                )}
                {hasFlag("has_wall_mounting") && (
                    <SingleSelect
                        label="Wall-mounted"
                        options={[
                            { value: "all", label: "All", tooltip: "Show all combinations regardless of wall mounting" },
                            { value: "with", label: "Yes", tooltip: "Only show wall-mounted combinations (with suspension rail)" },
                            { value: "without", label: "No", tooltip: "Only show freestanding combinations (no suspension rail)" },
                        ]}
                        value={hasWallMounting === true ? "with" : hasWallMounting === false ? "without" : "all"}
                        onChange={(v) => setHasWallMounting(v === "with" ? true : v === "without" ? false : null)}
                        tooltip="Filter by wall mounting (suspension rail)"
                    />
                )}
                {hasFrameTypes && (
                    <MultiSelect
                        label="Frame Type"
                        options={anchorConfig?.frame_type_categories || ALL_FRAME_TYPES}
                        selected={effectiveFrameTypes}
                        onChange={(types) => {
                            const allCats = anchorConfig?.frame_type_categories || ALL_FRAME_TYPES;
                            const defaults = anchorConfig?.default_frame_types ?? allCats;
                            const isDefault = types.length === defaults.length && defaults.every(d => types.includes(d));
                            setFrameTypes(isDefault ? [] : types);
                        }}
                        placeholder="Select frame types..."
                        allSelectedText="All frame types"
                        formatOption={(t) => ({ regular: "Regular", corner: "Corner", end: "End unit", mid: "Mid unit", height_extension: "Height extension", tv: "TV bench" }[t] || t)}
                        tooltip="Filter combinations by frame type"
                        optionTooltips={{
                            regular: "Standard frames (wardrobes, storage units)",
                            corner: "Corner units placed at 90° between two frame runs",
                            end: "End units placed at 90° at the end of a frame run, covering the side",
                            mid: "Narrow frames placed between two wardrobe frames",
                            tv: "TV benches and TV storage units",
                        }}
                    />
                )}
            </div>
                  </>
                )}
            </div>

            {(displayedBudgetEstimate || queryBudgetUnavailable) && (
                <div className={styles.budgetNotice}>
                    <QueryBudgetNotice
                        estimate={displayedBudgetEstimate}
                        error={queryBudgetUnavailable ? queryEstimateError : null}
                    />
                </div>
            )}

            {dataError && (
                <div className={styles.error}>
                    <h2>Error loading data</h2>
                    <p>{dataError.message}</p>
                </div>
            )}

            {!dataError && (stats || statsLoading) && (
                <div className={styles.summaryBar} style={statsLoading ? { opacity: 0.5 } : undefined}>
                    <span>
                        <HelpHint text="Number of receipts in the current selection">
                            Total receipts
                        </HelpHint>: <strong>{stats ? stats.total_combinations.toLocaleString() : '…'}</strong>
                    </span>
                    {periodDateRange(period) && (
                        <span className={styles.summaryPeriod}>
                            {periodDateRange(period)}
                        </span>
                    )}
                    {stats?.avg_value_per_combo != null && (
                        <span>
                            <HelpHint text="Average full combination value across matching receipts. Includes frames and all companion items in revenue, even items excluded from component analysis tabs (e.g. the BESTÅ support leg is excluded from the Legs tab but still counted here).">
                                Avg value
                            </HelpHint>: <strong>{Math.round(stats.avg_value_per_combo).toLocaleString()} {stats.currency}</strong>
                        </span>
                    )}
                </div>
            )}
            <p className={styles.percentageNote}>
                Percentages are shares of receipts for exact combinations, including item counts. Because there are
                many possible combinations, the top combination can still be only a small share of the total.
            </p>

            {!dataError && (loading ? (
                <LoadingSpinner message="Loading top combinations..." />
            ) : (<>
                {/* Desktop: table layout */}
                <div className={styles.tableContainer}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th className={styles.rankCol}>#</th>
                                <th className={styles.comboCol}>Combination</th>
                                <th
                                    className={`${styles.numCol} ${styles.sortableCol}`}
                                    onClick={() => setSortBy("quantity")}
                                >
                                    <HelpHint text={COMBO_QUANTITY_COL}>
                                        Receipts
                                    </HelpHint>{" "}
                                    <span className={sortBy === "quantity" ? styles.sortArrowActive : styles.sortArrow}>▼</span>
                                </th>
                                <th className={styles.numCol}>
                                    <HelpHint text={SHARE_PCT_COL}>
                                        %
                                    </HelpHint>
                                </th>
                                <th
                                    className={`${styles.numCol} ${styles.sortableCol}`}
                                    onClick={() => setSortBy("revenue")}
                                >
                                    <HelpHint text={REVENUE_COL_TOOLTIP}>
                                        Revenue (€)
                                    </HelpHint>{" "}
                                    <span className={sortBy === "revenue" ? styles.sortArrowActive : styles.sortArrow}>▼</span>
                                </th>
                                {localCurrency && (
                                    <th className={styles.numCol} title={`Total revenue (combo value × quantity sold), ${localCurrency}`}>
                                        Revenue ({localCurrency})
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCombinations.map((combo, index) => (
                                <tr key={combo.combination_id}>
                                    <td className={styles.rankCol}>{index + 1}</td>
                                    <td className={styles.comboCol}>
                                        <div className={styles.items}>
                                            {getDisplayItems(combo).map((item, i) => (
                                                <span key={i} className={`${styles.item} ${item.isAnchor ? styles.anchorItem : ""}`}>
                                                    {imageUrls[item.itemNo] && (
                                                        <img
                                                            src={imageUrls[item.itemNo].thumb}
                                                            alt=""
                                                            className={styles.itemImg}
                                                            loading="lazy"
                                                            onClick={() => onThumbClick(imageUrls[item.itemNo].preview)}
                                                            onMouseEnter={(e) => onThumbEnter(e, imageUrls[item.itemNo].preview)}
                                                            onMouseLeave={onThumbLeave}
                                                        />
                                                    )}
                                                    <span className={item.qty > 1 ? styles.qty : styles.qtyOne}>{item.qty}×</span>
                                                    <ItemNoInfo items={item.itemNo ? [{ item_no: item.itemNo }] : undefined}>
                                                        {item.name || item.itemNo}{item.productType && <span className={styles.productType}>, {item.productType}</span>}{item.color && <span className={styles.color}>, {normalizeColourSpelling(item.color)}</span>}
                                                    </ItemNoInfo>
                                                </span>
                                            ))}
                                            {combo.has_wall_mounting && (
                                                <span
                                                    className={styles.wallBadge}
                                                    title="Wall-mounted (includes suspension rail)"
                                                >
                                                    Wall mounted
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className={styles.numCol}>{combo.sales_count.toLocaleString()}</td>
                                    <td className={styles.numCol}>{combo.percentage < 0.01 ? '<0.01%' : (combo.percentage < 0.05 ? combo.percentage.toFixed(2) : combo.percentage.toFixed(1)) + '%'}</td>
                                    <td className={styles.numCol}>{combo.accumulated_revenue_eur != null ? `€${Math.round(combo.accumulated_revenue_eur).toLocaleString()}` : '—'}</td>
                                    {localCurrency && (
                                        <td className={styles.numCol}>
                                            {combo.accumulated_revenue != null
                                                ? combo.accumulated_revenue.toLocaleString(undefined, {
                                                    style: "currency",
                                                    currency: localCurrency,
                                                    maximumFractionDigits: 0,
                                                })
                                                : '—'}
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Mobile: card layout */}
                <div className={styles.cardList}>
                    {filteredCombinations.map((combo, index) => (
                        <div key={combo.combination_id} className={styles.card}>
                            <div className={styles.cardHeader}>
                                <span className={styles.cardRank}>#{index + 1}</span>
                                <span className={styles.cardStat}>Receipts: {combo.sales_count.toLocaleString()}</span>
                                <span className={styles.cardStat}>Share: {combo.percentage < 0.01 ? '<0.01%' : (combo.percentage < 0.05 ? combo.percentage.toFixed(2) : combo.percentage.toFixed(1)) + '%'}</span>
                                {combo.accumulated_revenue_eur != null && (
                                    <span className={styles.cardStat}>€{Math.round(combo.accumulated_revenue_eur).toLocaleString()}</span>
                                )}
                                {localCurrency && combo.accumulated_revenue != null && (
                                    <span className={styles.cardStat}>
                                        {combo.accumulated_revenue.toLocaleString(undefined, {
                                            style: "currency",
                                            currency: localCurrency,
                                            maximumFractionDigits: 0,
                                        })}
                                    </span>
                                )}
                            </div>
                            <div className={styles.cardItems}>
                                {getDisplayItems(combo).map((item, i) => (
                                    <div key={i} className={`${styles.cardItem} ${item.isAnchor ? styles.anchorItem : ""}`}>
                                        <div className={styles.cardItemTop}>
                                            {imageUrls[item.itemNo] && (
                                                <img
                                                    src={imageUrls[item.itemNo].thumb}
                                                    alt=""
                                                    className={styles.itemImg}
                                                    loading="lazy"
                                                    onClick={() => onThumbClick(imageUrls[item.itemNo].preview)}
                                                />
                                            )}
                                            <span className={item.qty > 1 ? styles.qty : styles.qtyOne}>{item.qty}×</span>
                                            <ItemNoInfo className={styles.cardItemName} items={item.itemNo ? [{ item_no: item.itemNo }] : undefined}>
                                                {item.name || item.itemNo}
                                            </ItemNoInfo>
                                        </div>
                                        {(item.productType || item.color) && (
                                            <div className={styles.cardItemDetail}>
                                                {item.productType && <span className={styles.productType}>{item.productType}</span>}
                                                {item.color && (
                                                    <span className={styles.cardItemColor}>
                                                        {normalizeColourSpelling(item.color)}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                                {combo.has_wall_mounting && (
                                    <span
                                        className={styles.wallBadge}
                                        title="Wall-mounted (includes suspension rail)"
                                    >
                                        Wall mounted
                                    </span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </>))}

            {hoverImg && (
                <img
                    src={hoverImg.src}
                    alt=""
                    className={styles.imgPreview}
                    style={{ left: hoverImg.x, top: hoverImg.y }}
                />
            )}

            {clickPreview && (
                <div className={styles.imgOverlay} onClick={() => setClickPreview(null)}>
                    <img src={clickPreview} alt="" className={styles.imgPreviewModal} />
                </div>
            )}
        </div>
    );
}
